import { type Object3D } from 'three'
import { ceil } from './math'
import { ThreeItemKeys, ThreeOriginalFileTypes } from '../constants/three'

export const SCENE_JSON_NAN_PLACEHOLDER = '__NaN__'

export const getObjectsByUserData = (object: Object3D | null, name: string, value: unknown) => {
  const result: Object3D[] = []

  if (!object) {
    return result
  }

  if (object.userData[name] === value) result.push(object)

  const children = object.children

  for (let i = 0, l = children.length; i < l; i++) {
    result.push(...getObjectsByUserData(children[i], name, value))
  }

  return result
}

export const getObjectsByProperties = (object: Object3D | null, callback: (object: Object3D) => boolean) => {
  const result: Object3D[] = []

  if (object && callback(object)) result.push(object)

  const children = object?.children || []

  for (let i = 0, l = children.length; i < l; i++) {
    result.push(...getObjectsByProperties(children[i], callback))
  }

  return result
}

export const getObjectByUserData = (object: Object3D | null, name: string, value: unknown) => {
  return getObjectsByUserData(object, name, value)[0]
}

export const convertToSceneUnit = (value?: number) => {
  if (value === undefined) return undefined
  return ceil(value / 1000, 3)
}

export const convertToUserUnit = (value?: number) => {
  if (typeof value === 'undefined') return undefined
  return ceil(value * 1000)
}

export const parse = (scene: string) => {
  // A JSON.parse reviver deoptimizes the entire native parse (~15s vs <1s on a
  // ~115MB scene, and heavy GC pressure). Only pay it when the NaN placeholder is
  // actually present — a cheap single substring scan decides. Backward compatible:
  // legacy scenes containing '__NaN__' still take the reviver path.
  if (!scene.includes(SCENE_JSON_NAN_PLACEHOLDER)) {
    return JSON.parse(scene)
  }

  return JSON.parse(scene, (_key: string, value: unknown) => {
    if (value === SCENE_JSON_NAN_PLACEHOLDER) {
      return NaN
    }
    return value
  })
}

export const isObjectDxf2D = (object: Object3D) => {
  return object.userData[ThreeItemKeys.ORIGINAL_FILE_TYPE] === ThreeOriginalFileTypes.DXF_2D
}
